import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/authContext';
import { Link } from 'react-router-dom';
import { addProject, onProjectsChange } from '../../firebase/projects';
import { Plus, X, MapPin, Square } from 'lucide-react';

const AddProjectModal = ({ isOpen, onClose, currentUser }) => {
    const [projectName, setProjectName] = useState('');
    const [projectImageUrl, setProjectImageUrl] = useState('');
    const [uploadMethod, setUploadMethod] = useState('file');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setProjectImageUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };
    
    const resetForm = () => {
        setProjectName('');
        setProjectImageUrl('');
        setUploadMethod('file');
        setError('');
        setIsSubmitting(false);
    }

    const handleAddProject = async (e) => {
        e.preventDefault();
        if (!projectName.trim()) {
            setError('Project name is required.');
            return;
        }
        setIsSubmitting(true);
        setError('');

        const newProject = {
            name: projectName,
            imageUrl: projectImageUrl,
        };

        try {
            await addProject(currentUser, newProject);
            resetForm();
            onClose();
        } catch (err) {
            setError('Failed to add project. Please try again.');
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-md relative">
                <button onClick={() => { resetForm(); onClose(); }} className="absolute top-3 right-3 p-1 rounded-full text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                    <X className="h-5 w-5" />
                </button>
                <form onSubmit={handleAddProject} className="space-y-4">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Add New Project</h2>
                    <div>
                        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Project Name</label>
                        <input
                            type="text"
                            value={projectName}
                            onChange={(e) => setProjectName(e.target.value)}
                            required
                            placeholder="My Awesome Project"
                            className="w-full mt-2 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block mb-2">Project Image (Optional)</label>
                        <div className="flex items-center space-x-4">
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="radio"
                                    name="uploadMethod"
                                    value="file"
                                    checked={uploadMethod === 'file'}
                                    onChange={() => { setUploadMethod('file'); setProjectImageUrl(''); }}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                />
                                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Upload File</span>
                            </label>
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="radio"
                                    name="uploadMethod"
                                    value="url"
                                    checked={uploadMethod === 'url'}
                                    onChange={() => { setUploadMethod('url'); setProjectImageUrl(''); }}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                />
                                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">From URL</span>
                            </label>
                        </div>

                        <div className="mt-4">
                            {uploadMethod === 'file' ? (
                                <input
                                    type="file"
                                    key={Date.now()}
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:text-gray-400 dark:file:bg-gray-700 dark:file:text-gray-300 dark:hover:file:bg-gray-600 cursor-pointer"
                                />
                            ) : (
                                <input
                                    type="url"
                                    value={projectImageUrl}
                                    onChange={(e) => setProjectImageUrl(e.target.value)}
                                    placeholder="https://example.com/image.png"
                                    className="w-full px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600"
                                />
                            )}
                        </div>
                    </div>
                    {error && <p className="text-red-600 text-sm">{error}</p>}
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`w-full px-4 py-2 text-white font-medium rounded-lg ${isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                        {isSubmitting ? 'Adding...' : 'Add Project'}
                    </button>
                </form>
            </div>
        </div>
    );
}

const Projects = () => {
    const { currentUser } = useAuth();
    const [projects, setProjects] = useState([]);
    const [isAddProjectModalOpen, setIsAddProjectModalOpen] = useState(false);
    
    useEffect(() => {
        if (currentUser) {
            const unsubscribe = onProjectsChange(currentUser.uid, (newProjects) => {
                setProjects(newProjects);
            });
            return () => unsubscribe();
        }
    }, [currentUser]);

    const canAddProjects = currentUser?.role && ['superadmin', 'admin'].includes(currentUser.role);

    const placeholderImage = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNjAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iI0U1RTdFQiIgLz48ZyB0cmFuc2Zvcm09InRyYW5zbGF0ZSgyNTAsIDE1MCkgc2NhbGUoNCkiPjxyZWN0IHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgeD0iMyIgeT0iMyIgcng9IjIiIHJ5PSIyIiBmaWxsPSJub25lIiBzdHJva2U9IiM5Q0EzQUYiIHN0cm9rZS13aWR0aD0iMS41Ii8+PGNpcmNsZSBjeD0iOSIgY3k9IjkiIHI9IjIiIGZpbGw9IiM5Q0EzQUYiLz48cGF0aCBkPSJtMjEgMTUtMy4wODYtMy4wODZhMiAyIDAgMCAwLTIuODI4IDBMNiAyMSIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjOUNBM0FGIiBzdHJva2Utd2lkdGg9IjEuNSIvPjwvZz48L3N2Zz4=";
    
    const statusColor = (status) => {
        switch (status) {
            case 'Completed':
                return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300';
            case 'In Progress':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300';
            case 'On Hold':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300';
            case 'Planning':
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
        }
    };
    
    return (
        <div className="w-full h-full flex flex-col pt-16">
            <div className="flex justify-between items-center px-4 sm:px-6 lg:px-8 py-6 w-full max-w-6xl mx-auto flex-shrink-0">
                <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-200">Projects</h1>
                {canAddProjects && (
                    <button
                        onClick={() => setIsAddProjectModalOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition-colors"
                        aria-label="Add new project"
                    >
                        <Plus className="h-5 w-5" />
                        <span className="hidden sm:inline">Add Project</span>
                    </button>
                )}
            </div>
            
            <div className="flex-grow min-h-0 overflow-y-auto px-4 sm:px-6 lg:px-8">
                <div className="w-full max-w-6xl mx-auto pb-8">
                    {projects.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {projects.map((project) => (
                                 <Link to={`/projects/${project.id}`} key={project.id} className="group block bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden border border-gray-200 dark:border-gray-700 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                                    <div className="relative">
                                        <img 
                                            src={project.imageUrl || placeholderImage} 
                                            alt={project.name} 
                                            className="w-full h-48 object-cover bg-gray-100 dark:bg-gray-700"
                                            data-ai-hint="project preview"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = placeholderImage;
                                            }}
                                        />
                                    </div>
                                    <div className="p-4">
                                        <div className="flex justify-between items-start">
                                            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 truncate pr-2 flex-1">{project.name}</h3>
                                            {project.status && (
                                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusColor(project.status)} flex-shrink-0`}>
                                                    {project.status}
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-2 space-y-2 text-sm text-gray-500 dark:text-gray-400">
                                            {project.location && (
                                                <a
                                                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(project.location)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="flex items-center gap-2 hover:text-blue-600 dark:hover:text-blue-400"
                                                >
                                                    <MapPin className="h-4 w-4 flex-shrink-0" />
                                                    <span className="truncate group-hover:underline">{project.location}</span>
                                                </a>
                                            )}
                                            {project.area && (
                                                <div className="flex items-center gap-2">
                                                    <Square className="h-4 w-4 flex-shrink-0" />
                                                    <span>{project.area} m²</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-20 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                           <p className="text-lg">No projects yet.{canAddProjects && " Add one to get started!"}</p>
                        </div>
                    )}
                </div>
            </div>

            <AddProjectModal
                isOpen={isAddProjectModalOpen}
                onClose={() => setIsAddProjectModalOpen(false)}
                currentUser={currentUser}
            />
        </div>
    );
};

export default Projects;
